import type { Resource } from '@toolproof-core/genesis/_contract/_index';

import { CONSTANTS } from '@toolproof-core/lib/_data/lookups';
import type { MutableToolStep } from '@toolproof-core/lib/_contract/workflow-overlay';

import {
    defineConnectorItemDerivation,
    defineEntityItemDerivation,
} from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import {
    buildLineGuideShape as buildVisualizationLineGuideShape,
    buildSphereShape,
} from '@toolproof-core/visualization/_pure/shapes';
import type { ConnectorItemDeclaration, EntityItemDeclaration } from '@toolproof-core/visualization/_contract/declaration';
import type {
    ConnectorItem,
    EntityItem,
    EntityItemParametricState,
} from '@toolproof-core/visualization/_contract/item';
import type { Shape } from '@toolproof-core/visualization/_contract/shape';
import type {
    ConnectorItemDerivation,
    EntityItemDerivation,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import {
    buildCaseItemKey,
    buildMacroStepConnectorItemKey,
    buildResourceTypeRadialConnectorItemKey,
    buildRoleItemKey,
    buildToolItemKey,
    buildToolRoleConnectorItemKey,
    buildToolStepConstellationKey,
    buildToolStepItemKey,
    buildWorkflowStateEntryConnectorItemKey,
    buildWorkflowStateEntryItemKey,
    type WorkflowKeyScope,
} from '@/workflow/_pure/keyCodec';
import type { BaseConfig } from '@/workflow/_contract/config';
import type { RoleDirection } from '@/workflow/_contract/role';
import type { ToolStepLayoutConfig } from '@/workflow/_pure/config/derivationConfig';

const TYPE_Tool = CONSTANTS.Handles.ResourceTypes.TYPE_Tool;
const TYPE_ResourceType = CONSTANTS.Handles.ResourceTypes.TYPE_ResourceType;

type ConnectorDisplayConfig = ConnectorItemDeclaration;

function buildDefaultSphereShape() {
    return buildSphereShape({ radius: 0.6 });
}

function buildLineGuideShape(): ConnectorItem['shape'] {
    return buildVisualizationLineGuideShape();
}

function deriveConnectorItemParametricState(config: ConnectorDisplayConfig) {
    return { ...config };
}

type ConstellationItemDefaults = EntityItemDeclaration;

function withLabelText(
    label: ConstellationItemDefaults['label'],
    text: string,
    backText?: string,
): EntityItemParametricState['label'] {
    if (label === undefined) return {};
    return {
        ...(label.aboveItem === undefined ? {} : { aboveItem: { ...label.aboveItem, text } }),
        ...(label.onItem === undefined ? {} : { onItem: { ...label.onItem, text } }),
        ...(label.backItem === undefined ? {} : { backItem: { ...label.backItem, text: backText ?? text } }),
    };
}

function deriveEntityItemParametricState(args: {
    itemDefaults: ConstellationItemDefaults | undefined;
    text: string;
    backText?: string;
    shape?: Shape;
}): EntityItemParametricState {
    const { itemDefaults, text, backText, shape } = args;
    if (itemDefaults === undefined) {
        return shape === undefined ? {} : { shape };
    }

    const { label, shape: declaredShape, ...rest } = itemDefaults;
    const effectiveShape = shape ?? declaredShape;

    return {
        ...rest,
        ...(effectiveShape === undefined ? {} : { shape: effectiveShape }),
        label: withLabelText(label, text, backText),
    };
}

function resolveResourceItemName(resource: Resource): string {
    const projection = resource.projection;
    if (typeof projection === 'boolean' || typeof projection === 'number') {
        return String(projection);
    }
    if (projection !== null && typeof projection === 'object') {
        const name = (projection as Record<string, unknown>).name;
        if (typeof name === 'string' && name.length > 0) return name;
    }
    throw new Error(`Resource '${resource.id}' projection must be a number, boolean, or named object.`);
}

export function toToolStepEntityItemDerivation(args: {
    toolStep: MutableToolStep;
    config: ToolStepLayoutConfig;
}): EntityItemDerivation {
    const { toolStep, config } = args;
    const label = toolStep.stepKey;
    const itemKey = buildToolStepItemKey(toolStep.stepKey);
    const item: EntityItem = {
        itemKind: 'entity',
        itemKey,
        name: label,
        description: '',
    };
    const { label: labelDeclaration, ...itemStyle } = config.item;
    const aboveItem = labelDeclaration?.aboveItem;
    const declaredOffset = aboveItem?.placement?.placementKind === 'anchor'
        ? aboveItem.placement.localOffset
        : aboveItem?.placement?.offset;

    return defineEntityItemDerivation({
        item,
        parametricState: {
            ...itemStyle,
            label: {
                aboveItem: {
                    ...(aboveItem ?? {}),
                    text: label,
                    placement: {
                        placementKind: 'anchor',
                        anchorRef: {
                            endpointKind: 'parametric',
                            parametricKind: 'ring-intersection',
                            ringItemKey: itemKey,
                            anglePolicy: {
                                anglePolicyKind: 'match-item-in-ring',
                                matchItemKey: itemKey,
                            },
                        },
                        ...(declaredOffset === undefined ? {} : { localOffset: declaredOffset }),
                    },
                    orientation: { orientationKind: 'billboard' },
                },
            },
        },
    });
}

export function toItemDerivation(resource: Resource, config: BaseConfig): EntityItemDerivation {
    const projectionValue = resource.projection;
    const projection =
        projectionValue !== null && typeof projectionValue === 'object'
            ? (projectionValue as Record<string, unknown>)
            : {};

    const handle =
        typeof projection.handle === 'string' && projection.handle.length > 0
            ? projection.handle
            : undefined;

    const key = `item:entity:${handle ?? resource.id}` as EntityItem['itemKey'];

    const name = resolveResourceItemName(resource);

    const description =
        typeof projection.description === 'string' ? projection.description : '';

    const symbol =
        typeof projection.symbol === 'string' && projection.symbol.length > 0
            ? projection.symbol
            : undefined;

    const shape = (() => {
        if (resource.resourceTypeHandle === TYPE_Tool) {
            return undefined;
        }

        if (resource.resourceTypeHandle === TYPE_ResourceType) {
            return config.domainPresentation.entities.resourceTypes.item?.shape
                ?? buildDefaultSphereShape();
        }

        return buildDefaultSphereShape();
    })();

    const itemDefaults = resource.resourceTypeHandle === TYPE_Tool
        ? config.domainPresentation.entities.tools.item
        : resource.resourceTypeHandle === TYPE_ResourceType
            ? config.domainPresentation.entities.resourceTypes.item
            : undefined;
    const item: EntityItem = {
        itemKind: 'entity',
        itemKey: key,
        name,
        description,
    };

    return defineEntityItemDerivation({
        item,
        parametricState: deriveEntityItemParametricState({
            itemDefaults,
            text: name,
            backText: symbol,
            shape,
        }),
    });
}

export function toRoleItemDerivation(args: {
    toolHandle: string;
    direction: RoleDirection;
    resourceTypeHandle: string;
    roleName: string;
    role: unknown;
    config: BaseConfig;
}): EntityItemDerivation {
    const { toolHandle, direction, resourceTypeHandle, roleName, role, config } = args;
    const roleRecord = role !== null && typeof role === 'object'
        ? (role as Record<string, unknown>)
        : {};

    const roleLabel =
        typeof roleRecord.name === 'string' && roleRecord.name.length > 0
            ? roleRecord.name
            : roleName;
    const roleDescription =
        typeof roleRecord.description === 'string'
            ? roleRecord.description
            : '';
    const item: EntityItem = {
        itemKind: 'entity',
        itemKey: buildRoleItemKey({
            toolHandle,
            direction,
            resourceTypeHandle,
            roleName,
        }),
        name: roleLabel,
        description: roleDescription,
    };

    return defineEntityItemDerivation({
        item,
        parametricState: deriveEntityItemParametricState({
            itemDefaults: config.domainPresentation.entities.roles[direction].item,
            text: roleLabel,
        }),
    });
}

export function toToolRoleConnectorItemDerivation(args: {
    toolHandle: string;
    direction: RoleDirection;
    resourceTypeHandle: string;
    roleName: string;
    config: BaseConfig;
}): ConnectorItemDerivation {
    const { toolHandle, direction, resourceTypeHandle, roleName, config } = args;
    const itemKey = buildToolRoleConnectorItemKey({
        toolHandle,
        direction,
        resourceTypeHandle,
        roleName,
    });
    const toolItemKey = buildToolItemKey(toolHandle);
    const roleItemKey = buildRoleItemKey({
        toolHandle,
        direction,
        resourceTypeHandle,
        roleName,
    });

    const item: ConnectorItem = {
        itemKind: 'connector',
        itemKey,
        shape: buildLineGuideShape(),
        endpoints: {
            from: {
                endpointKind: 'static',
                itemKey: toolItemKey,
                anchorKey: 'innerArcMid',
            },
            to: {
                endpointKind: 'static',
                itemKey: roleItemKey,
                anchorKey: 'center',
            },
        },
    };

    return defineConnectorItemDerivation({
        item,
        parametricState: deriveConnectorItemParametricState(
            config.domainPresentation.connectors.toolRole,
        ),
    });
}

/** Creates one side of a ResourceType-to-ToolStep radial slot. */
export function toResourceTypeRadialConnectorItemDerivation(args: {
    resourceTypeItemKey: EntityItem['itemKey'];
    toolStepKey: string;
    direction: 'input' | 'output';
    fromRingItemKey: EntityItem['itemKey'];
    toRingItemKey: EntityItem['itemKey'];
    fromRadialOffset?: number;
    toRadialOffset?: number;
    config: BaseConfig;
}): ConnectorItemDerivation {
    const {
        resourceTypeItemKey,
        toolStepKey,
        direction,
        fromRingItemKey,
        toRingItemKey,
        fromRadialOffset,
        toRadialOffset,
        config,
    } = args;

    const itemKey = buildResourceTypeRadialConnectorItemKey({
        resourceTypeItemKey,
        toolStepKey,
        direction,
    });

    const guideConfig = config.domainPresentation.connectors.resourceTypeRadial;

    const item: ConnectorItem = {
        itemKind: 'connector',
        itemKey,
        shape: buildLineGuideShape(),
        endpoints: {
            from: {
                endpointKind: 'parametric',
                parametricKind: 'ring-intersection',
                ringItemKey: fromRingItemKey,
                anglePolicy: {
                    anglePolicyKind: 'match-item-in-ring',
                    matchItemKey: resourceTypeItemKey,
                },
                ...(fromRadialOffset === undefined ? {} : { radialOffset: fromRadialOffset }),
            },
            to: {
                endpointKind: 'parametric',
                parametricKind: 'ring-intersection',
                ringItemKey: toRingItemKey,
                anglePolicy: {
                    anglePolicyKind: 'match-item-in-ring',
                    matchItemKey: resourceTypeItemKey,
                },
                ...(toRadialOffset === undefined ? {} : { radialOffset: toRadialOffset }),
            },
        },
    };

    return defineConnectorItemDerivation({
        item,
        parametricState: deriveConnectorItemParametricState(guideConfig),
    });
}

export function toMacroStepConnectorItemDerivation(args: {
    logicalMacroStepKey: string;
    startToolStepKey: string;
    endToolStepKey: string;
    startToolStepItemKey: EntityItem['itemKey'];
    endToolStepItemKey: EntityItem['itemKey'];
    laneAngle: number;
    macroStepKind: 'branch' | 'loop';
    startStepIndex: number;
    endStepIndex: number;
    config: BaseConfig;
    endRole?: 'when' | 'what';
    workflowKeyScope?: WorkflowKeyScope;
}): ConnectorItemDerivation {
    const {
        logicalMacroStepKey,
        startToolStepItemKey,
        endToolStepItemKey,
        laneAngle,
        config,
    } = args;
    const connectorConfig = config.domainPresentation.connectors.macroStep;

    const item: ConnectorItem = {
        itemKind: 'connector',
        itemKey: buildMacroStepConnectorItemKey({
            logicalMacroStepKey,
        }),
        shape: buildLineGuideShape(),
        endpoints: {
            from: {
                endpointKind: 'parametric',
                parametricKind: 'ring-intersection',
                ringItemKey: startToolStepItemKey,
                anglePolicy: {
                    anglePolicyKind: 'explicit',
                    angle: laneAngle,
                },
                radialOffset: connectorConfig.radialOffset,
            },
            to: {
                endpointKind: 'parametric',
                parametricKind: 'ring-intersection',
                ringItemKey: endToolStepItemKey,
                anglePolicy: {
                    anglePolicyKind: 'explicit',
                    angle: laneAngle,
                },
                radialOffset: connectorConfig.radialOffset,
            },
        },
    };

    const parametricState = deriveConnectorItemParametricState(connectorConfig);
    const casePair = connectorConfig.casePair;
    const toolStepConstellationKey = args.workflowKeyScope
        ? buildToolStepConstellationKey(args.workflowKeyScope)
        : undefined;
    return defineConnectorItemDerivation({
        item,
        parametricState: args.endRole && casePair?.enabled && toolStepConstellationKey
            ? {
                ...parametricState,
                path: {
                    ...parametricState.path,
                    pathKind: 'curved',
                    curvePlane: 'xz',
                    // Reversing radial endpoints reverses the curve normal, not its outward side.
                    curveDirection: (args.macroStepKind !== connectorConfig.lowerAngle) === (args.endStepIndex > args.startStepIndex)
                        ? 'positive'
                        : 'negative',
                    curveStrength: casePair.curveStrength,
                    curveMinOffset: casePair.curveMinOffset,
                },
                visualStyle: {
                    ...parametricState.visualStyle,
                    activeHighlightColor: casePair.activeHighlightColor,
                },
                highlightOnActiveOf: [startToolStepItemKey, endToolStepItemKey].map((itemKey) => ({
                    constellationKey: toolStepConstellationKey,
                    itemKey,
                })),
            }
            : parametricState,
    });
}

export function toWorkflowStateEntryItemDerivation(args: {
    stepKey: string;
    direction: RoleDirection;
    resourceTypeHandle: string;
    roleName: string;
    config: BaseConfig;
}): EntityItemDerivation {
    const { stepKey, direction, resourceTypeHandle, roleName, config } = args;
    const panelConfig = config.domainPresentation.entities.workflowStateEntries.item;
    const sideStyle = direction === 'input' ? panelConfig.input : panelConfig.output;

    const item: EntityItem = {
        itemKind: 'entity',
        itemKey: buildWorkflowStateEntryItemKey({
            stepKey,
            direction,
            resourceTypeHandle,
            roleName,
        }),
        name: roleName,
        description: `${direction} workflow-state role`,
    };

    return defineEntityItemDerivation({
        item,
        parametricState: {
            shape: panelConfig.shape,
            ...sideStyle,
            label: {
                aboveItem: {
                    text: roleName,
                    ...(panelConfig.label?.aboveItem ?? {}),
                },
            },
        },
    });
}

export function toWorkflowStateEntryConnectorItemDerivation(args: {
    targetStepKey: string;
    targetRoleName: string;
    sourceStepKey: string;
    sourceRoleName: string;
    sourceDirection: RoleDirection;
    resourceTypeHandle: string;
    targetItemKey: EntityItem['itemKey'];
    sourceItemKey: EntityItem['itemKey'];
    config: BaseConfig;
}): ConnectorItemDerivation {
    const {
        targetStepKey,
        targetRoleName,
        sourceStepKey,
        sourceRoleName,
        sourceDirection,
        resourceTypeHandle,
        targetItemKey,
        sourceItemKey,
        config,
    } = args;

    const connectorConfig = config.domainPresentation.connectors.workflowStateEntry;
    const connectorEndpointFromSideVariant = sourceDirection === 'output'
        ? 'min'
        : 'max';
    const connectorEndpointToSideVariant = sourceDirection === 'output'
        ? 'max'
        : 'max';
    const connectorCurveDirection = sourceDirection === 'input'
        ? 'negative'
        : connectorConfig.path?.curveDirection;

    const item: ConnectorItem = {
        itemKind: 'connector',
        itemKey: buildWorkflowStateEntryConnectorItemKey({
            targetStepKey,
            targetRoleName,
            sourceStepKey,
            sourceRoleName,
            sourceDirection,
            resourceTypeHandle,
        }),
        shape: buildLineGuideShape(),
        endpoints: {
            from: {
                endpointKind: 'static',
                itemKey: targetItemKey,
                anchorKey: 'center',
            },
            to: {
                endpointKind: 'static',
                itemKey: sourceItemKey,
                anchorKey: 'center',
            },
        },
    };

    return defineConnectorItemDerivation({
        item,
        parametricState: {
            ...deriveConnectorItemParametricState({
                ...connectorConfig,
                path: {
                    ...(connectorConfig.path ?? {}),
                    ...(connectorCurveDirection === undefined
                        ? {}
                        : { curveDirection: connectorCurveDirection }),
                },
            }),
            endpointResolution: {
                fromSideVariant: connectorEndpointFromSideVariant,
                toSideVariant: connectorEndpointToSideVariant,
            },
        },
    });
}

export function toCaseItemDerivation(args: {
    ownerMacroStepKey: string;
    ownerCaseIndex: number;
    slotFamily: 'case' | 'cases';
    innerRadius: number;
    outerRadius: number;
    whenBoundary: 'inner' | 'outer';
    label: string;
    name: string;
    description?: string;
    colorNearWhen?: string;
    colorNearWhat?: string;
    opacity?: number;
    segments?: number;
    gradientCutoffCenter?: number;
    gradientCutoffWidth?: number;
}): EntityItemDerivation {
    const {
        ownerMacroStepKey,
        ownerCaseIndex,
        innerRadius,
        outerRadius,
        whenBoundary,
        name,
        description,
        colorNearWhen = '#e2e2e2',
        colorNearWhat = '#2a2a2a',
        opacity = 0.14,
        segments,
        gradientCutoffCenter,
        gradientCutoffWidth,
    } = args;

    const clampedInnerRadius = Math.max(0.0001, innerRadius);
    const clampedOuterRadius = Math.max(clampedInnerRadius + 0.0001, outerRadius);
    const innerColor = whenBoundary === 'inner' ? colorNearWhen : colorNearWhat;
    const outerColor = whenBoundary === 'outer' ? colorNearWhen : colorNearWhat;
    const midRadius = (clampedInnerRadius + clampedOuterRadius) / 2;

    const item: EntityItem = {
        itemKind: 'entity',
        itemKey: buildCaseItemKey({
            ownerMacroStepKey,
            ownerCaseIndex,
        }),
        name,
        ...(description === undefined ? {} : { description }),
    };

    return defineEntityItemDerivation({
        item,
        parametricState: {
            visualStyle: {
                color: colorNearWhat,
                opacity,
            },
            shape: {
                shapeKind: 'annular-band',
                labelHostCapability: 'above-and-on',
                innerRadius: clampedInnerRadius,
                outerRadius: clampedOuterRadius,
                ...(segments === undefined ? {} : { segments }),
                innerColor,
                outerColor,
                ...(typeof gradientCutoffCenter === 'number' ? { gradientCutoffCenter } : {}),
                ...(typeof gradientCutoffWidth === 'number' ? { gradientCutoffWidth } : {}),
                anchorsByKey: {
                    midRadiusCenter: { anchorKey: 'midRadiusCenter', localPosition: [0, 0, midRadius] },
                    innerRimCenter: { anchorKey: 'innerRimCenter', localPosition: [0, 0, clampedInnerRadius] },
                    outerRimCenter: { anchorKey: 'outerRimCenter', localPosition: [0, 0, clampedOuterRadius] },
                },
            },
        },
    });
}
